import Backbone from 'backbone'
import _ from 'lodash'
import BoxOptions from 'scoreboard/components/box-options'
import CalendarRangeSelector from 'scoreboard/components/calendar-range-selector'
import FormView from 'scoreboard/components/form-view'
import i18n from 'scoreboard/components/i18n'
import unwrapModel from 'scoreboard/components/unwrap-model'
import ChartDesignerBreakdown from 'scoreboard/charts/model/chart-designer-breakdown'
import ChartDesignerRange from 'scoreboard/charts/model/chart-designer-range'
import template from './edit-calendar-period-range-view.hbs'
import GroupedPeriodRangeSelector from './grouped-period-range-selector'
import calendars from 'scoreboard/components/calendars'

const EditCalendarPeriodRangeView = Backbone.View.extend(/** @lends EditCalendarPeriodRangeView.prototype */{
	tagName: 'form',

	className: 'edit-calendar-period-range-view',

	/**
	 * @type ChartDesignerRange
	 */
	model: null,

	/**
	 * @type ChartDesignerBreakdown
	 */
	breakdown: null,

	/**
	 * @constructs
	 *
	 * @param {Object} opt

	 * @param {ChartDesigner} opt.chartDesigner
	 *
	 * @param {ChartDesignerBreakdown} [opt.breakdown]
	 *
	 * @param {Boolean} [opt.isNewRange] - if the range is newly created. This
	 * is not the same as Backbone.Model.isNew(), because that relies on whether
	 * there is an ID (ie it has been saved to the database).
	 * options.isNewRange should be true if the range hasn't been created
	 * in the UI yet (like when clicking the "Add Range" button)
	 *
	 * @param {String} [opt.dialogTitle='charts.editRange'] title for the dialog
	 */
	initialize: function (opt = {}) {
		this.options = opt
		this.breakdown = opt.breakdown

		this.formView = new FormView({
			el: this.el,
			submit: this.submit.bind(this),
			allowCleanSubmission: this.options.isNewRange
		})
	},

	render: function() {
		this.$el.empty().append(template({
			title: i18n.get(this.options.dialogTitle || 'charts.editRange')
		}))

		this.renderBreakdownByOptions()
		this.renderRangeSelectors()

		this.formView.start()

		return this
	},

	renderBreakdownByOptions: function () {
		let breakdownBy = this.breakdown.get('breakdownBy')

		let container = this.$('.options-container')

		let options = {
			availableOptions: [ {
				value: 'calendar-period-ranges',
				title: 'charts.rangeType.date.standard',
				subLabel: 'charts.rangeType.date.standard.example',
				icon: 'chart-date-range-standard'
			},  ]
		}

		if (calendars.calendars.length >= 2) {
			options.availableOptions.push({
				value: 'calendar-period-grouped-ranges',
				title: 'charts.rangeType.date.group',
				subLabel: 'charts.rangeType.date.group.example',
				icon: 'chart-date-range-group'
			})
		}

		this.calendarPeriodBreakdownTypeSelector = new BoxOptions(options)

		container.append(this.calendarPeriodBreakdownTypeSelector.el)

		this.calendarPeriodBreakdownTypeSelector.render()

		// Select a value before adding listener for 'select' event, so we
		// don't trigger unnecessary logic
		this.calendarPeriodBreakdownTypeSelector.select(breakdownBy)

		this.listenTo(this.calendarPeriodBreakdownTypeSelector, 'select', t => {
			this.onSelectCalendarPeriodBreakdownType(t.value)
		})
	},

	onSelectCalendarPeriodBreakdownType: function (newBreakdownBy) {
		let oldBreakdownBy = this.breakdown.get('breakdownBy')

		if (newBreakdownBy === oldBreakdownBy) {
			return
		}

		this.breakdown.set('breakdownBy', newBreakdownBy)

		this.breakdown.ranges.reset()
		this.model.clear()
		this.model.set(ChartDesignerBreakdown.calendarPeriodBreakdownRange(this.breakdown))

		this.renderRangeSelectors()
		this.formView.change()
	},

	renderRangeSelectors: function () {
		_.invokeMap([this.calendarRangeSelector, this.groupedPeriodRangeSelector], 'remove')

		if (this.isGrouped()) {
			this.renderGroupedRangeSelector()
		} else {
			this.renderStandardRangeSelector()
		}
	},

	renderStandardRangeSelector: function () {
		let calendarRange = {
			calendarId: this.model.get('calendarId')
		}

		if (_.isNil(this.model.get('startPeriodId'))) {
			_.extend(calendarRange, {
				relative: true,
				first: this.model.get('startPeriodDelta'),
				last: this.model.get('endPeriodDelta')
			})
		} else {
			_.extend(calendarRange, {
				relative: false,
				first: this.model.get('startPeriodId'),
				last: this.model.get('endPeriodId')
			})
		}

		this.calendarRangeSelector = new CalendarRangeSelector({
			range: calendarRange,
			canChangeCalendar: true,
			allowAppCalendar: true
		})

		this.listenTo(this.calendarRangeSelector, 'change', () => this.applyCalendarRangeSelectorChanges())

		this.$('.panel-content').append(this.calendarRangeSelector.el)
		this.calendarRangeSelector.render()
	},

	renderGroupedRangeSelector: function () {
		this.groupedPeriodRangeSelector = new GroupedPeriodRangeSelector({
			// Send in a cloned model, because certain values like "Week by Day"
			// will set group/breakdown calendar IDs like 'weekly' or 'daily',
			// which doesn't fit with the model.
			// So we'll give GroupedPeriodRangeSelector its own copy of the model,
			// then update the "real" model later
			model: new ChartDesignerRange(_.cloneDeep(unwrapModel(this.model))),
			allowUndefinedCalendars: this.allowUndefinedCalendars()
		})

		this.$('.panel-content').append(this.groupedPeriodRangeSelector.el)
		this.groupedPeriodRangeSelector.render()

		this.listenTo(this.groupedPeriodRangeSelector, 'change', () => {
			this.formView.change()

			// Apply the changes from the cloned model in GroupedPeriodRangeSelector
			// to the "real" model
			let update = _.extend(
				_.cloneDeep(unwrapModel(this.groupedPeriodRangeSelector.model)),
				// Override with this so that summaryType is applied and
				// things like `groupCalendarId: 'weekly'` are set properly
				this.groupedPeriodRangeSelector.idsAndSummaryType())

			this.model.set(update)
		})
	},

	isGrouped: function () {
		return this.breakdown.get('breakdownBy') === 'calendar-period-grouped-ranges'
	},

	applyCalendarRangeSelectorChanges: function () {
		let calendarRange = this.calendarRangeSelector.val()
		let update = {
			calendarId: calendarRange.calendarId
		}

		if (calendarRange.relative) {
			update.startPeriodId = null
			update.endPeriodId = null
			update.startPeriodDelta = calendarRange.first
			update.endPeriodDelta = calendarRange.last
		} else {
			update.startPeriodId = calendarRange.first
			update.endPeriodId = calendarRange.last
			update.startPeriodDelta = null
			update.endPeriodDelta = null
		}

		this.model.set(update)

		this.formView.change()
	},

	/**
	 * See validGroupCalendars()
	 */
	allowUndefinedCalendars () {
		return true
	},

	submit: function () {
		this.trigger('done')
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([ this.calendarRangeSelector, this.formView ], 'remove')
	}

})

export default EditCalendarPeriodRangeView

EditCalendarPeriodRangeView.__filename__ = 'components/designer/edit-calendar-period-range-view.js'
